import type { ReactNode } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  countBy,
  filterUnits,
  platformKeys,
  type CuadreUnit,
  type UnitFilters,
} from "../../../features/sg/cuadre";
import { PLATFORMS, PLATFORM_META } from "../../../features/sg/platforms";
import { PlatformLogo } from "../components/PlatformLogo";
import { REPORT_DOT, fmt } from "./ui";

interface Props {
  units: CuadreUnit[];
  filters: UnitFilters;
  onChange: (patch: Partial<UnitFilters>) => void;
  isDarkMode: boolean;
}

// Cada opción muestra cuántas unidades quedarían al elegirla, con el resto de
// los filtros puestos. Ahí viven los números: no se repiten en otro lado.
export default function CuadreFilters({ units, filters, onChange, isDarkMode }: Props) {
  const { t } = useTranslation("sg");

  const facets = useMemo(
    () => ({
      billing: countBy(filterUnits(units, filters, "billing"), (u) => u.billing),
      report: (() => {
        const base = filterUnits(units, filters, "report");
        const c = countBy(base, (u) => u.report);
        return { ...c, silent: base.length - (c.ok ?? 0) } as Record<string, number>;
      })(),
      platform: countBy(filterUnits(units, filters, "platform"), platformKeys),
      condition: countBy(filterUnits(units, filters, "condition"), (u) => u.condition ?? "-"),
      check: countBy(filterUnits(units, filters, "check"), (u) => [...(u.dupImei ? ["dupImei"] : []), ...(u.tagMismatch ? ["tagMismatch"] : [])]),
    }),
    [units, filters]
  );

  const conditions = Object.keys(facets.condition)
    .filter((c) => c !== "-")
    .sort((a, b) => (facets.condition[b] ?? 0) - (facets.condition[a] ?? 0));

  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";

  return (
    <div className="space-y-6">
      <Group title={t("cuadre.filters.billing")} muted={muted}>
        {(["billable", "notBillable", "inactive"] as const).map((b) => (
          <Option
            key={b}
            active={filters.billing === b}
            count={facets.billing[b]}
            onClick={() => onChange({ billing: filters.billing === b ? "" : b })}
            isDarkMode={isDarkMode}
          >
            {t(`cuadre.billing.${b}`)}
          </Option>
        ))}
      </Group>

      <Group title={t("cuadre.filters.report")} muted={muted}>
        {(["ok", "silent", "week", "old", "none"] as const).map((r) => (
          <Option
            key={r}
            active={filters.report === r}
            count={facets.report[r]}
            indent={r === "week" || r === "old" || r === "none"}
            icon={r !== "silent" ? <span className={`w-2 h-2 rounded-full ${REPORT_DOT[r]}`} /> : undefined}
            onClick={() => onChange({ report: filters.report === r ? "" : r })}
            isDarkMode={isDarkMode}
          >
            {t(`cuadre.report.${r}`)}
          </Option>
        ))}
      </Group>

      <Group title={t("cuadre.filters.platform")} muted={muted}>
        {PLATFORMS.map((p) => (
          <Option
            key={p}
            active={filters.platform === p}
            count={facets.platform[p]}
            icon={<PlatformLogo platform={p} size={16} />}
            onClick={() => onChange({ platform: filters.platform === p ? "" : p })}
            isDarkMode={isDarkMode}
          >
            {PLATFORM_META[p].label}
          </Option>
        ))}
        {(["multi", "none"] as const).map((p) => (
          <Option
            key={p}
            active={filters.platform === p}
            count={facets.platform[p]}
            icon={<i className={`${p === "multi" ? "ri-stack-line text-amber-500" : "ri-question-line text-gray-400"} text-base leading-none`} />}
            onClick={() => onChange({ platform: filters.platform === p ? "" : p })}
            isDarkMode={isDarkMode}
          >
            {t(`cuadre.platform.${p}`)}
          </Option>
        ))}
      </Group>

      {/* Datos que no cuadran en SG mismo: sólo aparece si hay algo que revisar */}
      {(facets.check.dupImei || facets.check.tagMismatch || filters.check) && (
        <Group title={t("cuadre.filters.check")} muted={muted}>
          {(["dupImei", "tagMismatch"] as const).map((c) => (
            <Option
              key={c}
              active={filters.check === c}
              count={facets.check[c]}
              icon={<i className="ri-error-warning-line text-amber-500 text-base leading-none" />}
              onClick={() => onChange({ check: filters.check === c ? "" : c })}
              isDarkMode={isDarkMode}
            >
              {t(`cuadre.check.${c}`)}
            </Option>
          ))}
        </Group>
      )}

      {conditions.length > 0 && (
        <Group title={t("cuadre.filters.condition")} muted={muted}>
          {conditions.map((c) => (
            <Option
              key={c}
              active={filters.condition === c}
              count={facets.condition[c]}
              onClick={() => onChange({ condition: filters.condition === c ? "" : c })}
              isDarkMode={isDarkMode}
            >
              {c}
            </Option>
          ))}
          <Option
            active={filters.condition === "-"}
            count={facets.condition["-"]}
            onClick={() => onChange({ condition: filters.condition === "-" ? "" : "-" })}
            isDarkMode={isDarkMode}
          >
            {t("cuadre.filters.noCondition")}
          </Option>
        </Group>
      )}
    </div>
  );
}

function Group({ title, muted, children }: { title: string; muted: string; children: ReactNode }) {
  return (
    // min-w-0: un fieldset tiene min-width: min-content y no deja recortar etiquetas largas.
    <fieldset className="min-w-0">
      <legend className={`mb-1.5 text-[11px] font-semibold uppercase tracking-wider ${muted}`}>{title}</legend>
      <div className="space-y-0.5">{children}</div>
    </fieldset>
  );
}

function Option({
  active,
  count,
  icon,
  indent,
  onClick,
  isDarkMode,
  children,
}: {
  active: boolean;
  count: number | undefined;
  icon?: ReactNode;
  indent?: boolean;
  onClick: () => void;
  isDarkMode: boolean;
  children: ReactNode;
}) {
  const empty = !count && !active;
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      disabled={empty}
      className={`w-full min-h-[34px] flex items-center gap-2.5 rounded-lg px-2.5 text-left text-sm transition-colors duration-150 disabled:opacity-40 disabled:cursor-default ${
        indent ? "pl-6" : ""
      } ${
        active
          ? isDarkMode
            ? "bg-primary-500/15 text-white ring-1 ring-primary-500/40"
            : "bg-primary-500/10 text-gray-900 ring-1 ring-primary-500/40"
          : isDarkMode
            ? "text-gray-300 hover:bg-white/5"
            : "text-gray-700 hover:bg-gray-100"
      }`}
    >
      {icon && <span className="w-4 flex justify-center shrink-0">{icon}</span>}
      <span className="flex-1 min-w-0 truncate">{children}</span>
      <span className={`tabular-nums text-xs ${active ? "font-semibold" : isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{fmt(count)}</span>
    </button>
  );
}
